import React from 'react'
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, BarChart3, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Check if we're on the main page
  const isOnMainPage = location.pathname === '/';

  const scrollToSection = (sectionId: string) => {
    if (isOnMainPage) {
      // If on main page, scroll to section
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // If on other pages, navigate to main page with section anchor
      navigate(`/#${sectionId}`);
    }
    setIsMobileMenuOpen(false);
  };

  const goToHome = () => {
    navigate('/');
  };

  return (
    <header className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-b border-border-color shadow-sm z-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 w-full">
          {/* Logo */}
          <div 
            className="flex items-center gap-2 cursor-pointer flex-shrink-0"
            onClick={goToHome}
          >
            <img src="/tagnetica-badge.webp" alt="Tagnetica logo" className="h-8 w-8 object-contain rounded-lg p-1" />
            <span className="text-lg sm:text-xl font-bold text-text-primary">Tagnetica</span>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-1 text-text-secondary hover:text-primary transition-colors">
                  Tools
                  <ChevronDown className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem asChild>
                  <a 
                    href="https://gtmsuperhero.tagnetica.co.uk/" 
                    className="cursor-pointer"
                  >
                    GTM Super Hero
                  </a>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <button 
              onClick={() => scrollToSection('challenges')}
              className="text-text-secondary hover:text-primary transition-colors"
            >
              Challenges
            </button>
            <button 
              onClick={() => scrollToSection('services')}
              className="text-text-secondary hover:text-primary transition-colors"
            >
              Services
            </button>
            <button 
              onClick={() => scrollToSection('about')}
              className="text-text-secondary hover:text-primary transition-colors"
            >
              About
            </button>
            <button 
              onClick={() => scrollToSection('clients')}
              className="text-text-secondary hover:text-primary transition-colors"
            >
              Clients
            </button>
            <button 
              onClick={() => scrollToSection('contact')}
              className="text-text-secondary hover:text-primary transition-colors"
            >
              Contact
            </button>
          </nav>

          {/* Desktop CTA */}
          <div className="hidden md:block">
            <Button 
              onClick={() => scrollToSection('contact')}
              className="bg-primary hover:bg-primary/90 text-white font-semibold"
            >
              Get Started
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden flex-shrink-0 ml-2 z-10"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
          >
            {isMobileMenuOpen ? (
              <X className="h-6 w-6 text-text-primary" />
            ) : (
              <Menu className="h-6 w-6 text-text-primary" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-border-color -mx-4 sm:-mx-6 lg:-mx-8">
            <nav className="py-4 space-y-4 px-4 sm:px-6 lg:px-8">
              <div>
                <div className="text-text-secondary font-medium mb-2">Tools</div>
                <div className="pl-4 space-y-2">
                  <a 
                    href="https://gtmsuperhero.tagnetica.co.uk/" 
                    className="block text-text-secondary hover:text-primary transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    GTM Super Hero
                  </a>
                </div>
              </div>
              <button 
                onClick={() => scrollToSection('challenges')}
                className="block w-full text-left py-2 text-text-secondary hover:text-primary transition-colors"
              >
                Challenges
              </button>
              <button 
                onClick={() => scrollToSection('services')}
                className="block w-full text-left py-2 text-text-secondary hover:text-primary transition-colors"
              >
                Services
              </button>
              <button 
                onClick={() => scrollToSection('about')}
                className="block w-full text-left py-2 text-text-secondary hover:text-primary transition-colors"
              >
                About
              </button>
              <button 
                onClick={() => scrollToSection('clients')}
                className="block w-full text-left py-2 text-text-secondary hover:text-primary transition-colors"
              >
                Clients
              </button>
              <button 
                onClick={() => scrollToSection('contact')}
                className="block w-full text-left py-2 text-text-secondary hover:text-primary transition-colors"
              >
                Contact
              </button>
              <div className="pt-2">
                <Button 
                  onClick={() => scrollToSection('contact')}
                  className="w-full bg-primary hover:bg-primary/90 text-white font-semibold"
                >
                  Get Started
                </Button>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
